/**
 * TenantContext.js
 * Gestión segura del contexto de tenant
 *
 * Este contexto maneja el tenant (publisher/biller) activo del usuario.
 * El contexto se almacena en el backend y se valida con permisos.
 *
 * Uso:
 * - useTenant() - Hook para acceder al contexto
 * - setTenantContext(publisherId, billerId) - Establecer tenant activo
 * - clearTenantContext() - Limpiar contexto
 *
 * @see /opt/acmw/doc/21-ANALISIS-SEGURIDAD-TENANT-CONTEXT.md
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { USER_DATA_KEY } from '../constants';
import { useAuth } from './AuthContext';

const TenantContext = createContext();

// Generar un ID único para esta pestaña del navegador
const generateTabId = () => {
  let tabId = sessionStorage.getItem('ACMW_TAB_ID');
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('ACMW_TAB_ID', tabId);
  }
  return tabId;
};

const TAB_ID = generateTabId();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [tenantContext, setTenantContextState] = useState(null);
  const [allowedTenants, setAllowedTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar contexto inicial desde el backend
  const loadTenantContext = useCallback(async () => {
    const userData = localStorage.getItem(USER_DATA_KEY);
    if (!userData) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get('/auth/tenant-context', {
        headers: { 'X-Tab-ID': TAB_ID }
      });

      if (response.data.success) {
        setTenantContextState(response.data.data.tenantContext);
        setAllowedTenants(response.data.data.allowedTenants || []);
        setError(null);
      }
    } catch (err) {
      console.error('[TenantContext] Error loading context:', err);
      setError(err.response?.data?.message || 'Error cargando contexto');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Recargar contexto cuando cambia el estado de autenticación
  // FIX: Esto resuelve el bug donde el primer acceso del día se quedaba en "Cargando Dashboard... 0%"
  // porque loadTenantContext se ejecutaba en el mount sin token, y no se recargaba después del login
  useEffect(() => {
    if (isAuthenticated) {
      loadTenantContext();
    } else {
      // Limpiar estado cuando el usuario cierra sesión
      setTenantContextState(null);
      setAllowedTenants([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, loadTenantContext]);

  // Establecer contexto de tenant
  const setTenantContext = useCallback(async (publisherId, billerId) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post('/auth/set-tenant-context',
        { publisherId, billerId },
        { headers: { 'X-Tab-ID': TAB_ID } }
      );

      if (response.data.success) {
        const ctx = response.data.data.tenantContext;
        setTenantContextState(ctx);
        // Guardar en sessionStorage para que el interceptor de axios pueda acceder
        sessionStorage.setItem('ACMW_TENANT_CONTEXT', JSON.stringify({
          publisherId: ctx.publisherId,
          billerId: ctx.billerId,
          tenantKey: ctx.tenantKey
        }));
        return { success: true, tenantContext: ctx };
      }

      return { success: false, error: response.data.message };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error estableciendo contexto';
      setError(errorMessage);
      console.error('[TenantContext] Error setting context:', err);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Limpiar contexto
  const clearTenantContext = useCallback(async (clearAllTabs = false) => {
    try {
      await api.post('/auth/clear-tenant-context',
        { clearAllTabs },
        { headers: { 'X-Tab-ID': TAB_ID } }
      );
      setTenantContextState(null);
      return { success: true };
    } catch (err) {
      console.error('[TenantContext] Error clearing context:', err);
      return { success: false, error: err.response?.data?.message };
    }
  }, []);

  // Verificar si el usuario tiene acceso a un tenant específico
  const canAccessTenant = useCallback((publisherId, billerId) => {
    return allowedTenants.some(
      t => t.publisher_id === publisherId && t.biller_id === billerId
    );
  }, [allowedTenants]);

  // Obtener tenant key para un publisher/biller
  const getTenantKey = useCallback((publisherId, billerId) => {
    const tenant = allowedTenants.find(
      t => t.publisher_id === publisherId && t.biller_id === billerId
    );
    return tenant?.tenant_key || null;
  }, [allowedTenants]);

  // Actualizar contexto cuando cambia la URL (para sincronizar con navegación)
  const syncWithUrl = useCallback(async (publisherId, billerId) => {
    // Solo actualizar si es diferente al contexto actual
    if (tenantContext?.publisherId !== publisherId || tenantContext?.billerId !== billerId) {
      // Verificar que el usuario tiene acceso
      if (canAccessTenant(publisherId, billerId)) {
        await setTenantContext(publisherId, billerId);
      }
    }
  }, [tenantContext, canAccessTenant, setTenantContext]);

  const value = {
    // Estado
    tenantContext,
    allowedTenants,
    isLoading,
    error,
    tabId: TAB_ID,

    // Getters
    publisherId: tenantContext?.publisherId || null,
    billerId: tenantContext?.billerId || null,
    tenantKey: tenantContext?.tenantKey || null,
    publisherName: tenantContext?.publisherName || null,
    hasTenantContext: !!tenantContext,

    // Actions
    setTenantContext,
    clearTenantContext,
    loadTenantContext,
    syncWithUrl,

    // Helpers
    canAccessTenant,
    getTenantKey
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
